import { Injectable } from '@angular/core';
import Highcharts from 'highcharts/esm/highcharts';
import { NowcastingKpiState } from './kpi-state';

@Injectable()
export class KpiChartService {

  private static readonly LEVEL_COLORS: Record<number, string> = {
    2: '#F9A825', 3: '#E65100', 4: '#B71C1C',
  };

  private static readonly LEVEL_PALETTE: Record<number, string[]> = {
    2: ['#FFF176', '#FFEE58', '#FFD54F', '#FFCA28', '#FFC107', '#FFB300', '#FFA000', '#FF8F00', '#FF6F00'],
    3: ['#FF8A65', '#FF7043', '#FF5722', '#F4511E', '#E64A19', '#D84315', '#BF360C', '#A1360A', '#8B2E08'],
    4: ['#EF5350', '#E53935', '#D32F2F', '#C62828', '#B71C1C', '#8E0000', '#6D0000', '#520000', '#3B0000'],
  };

  private static readonly LEVEL_LABELS: Record<number, string> = {
    2: 'Moderado', 3: 'Fuerte', 4: 'Muy Fuerte',
  };

  private static readonly LAYER_SHORT: Record<number, string> = {
    0: 'Departamentos', 1: 'Provincias', 2: 'Distritos', 3: 'Centros Poblados',
    4: 'Predios Rurales', 5: 'Parcelas de Cultivo', 6: 'Prod. Agrícolas', 7: 'Prod. Pecuarios',
  };

  /** Capas con división administrativa: el conteo se deduplica. */
  private static readonly TERRITORIAL_LAYER_IDS: Record<number, true> = { 0: true, 1: true, 2: true };

  private static readonly LAYER_COLORS: string[] = [
    '#5C6BC0', '#26A69A', '#66BB6A', '#FFA726', '#EF5350',
    '#AB47BC', '#78909C', '#8D6E63', '#EC407A',
  ];

  // ── Shared helpers ─────────────────────────────────────────

  private tooltipBase(): Highcharts.TooltipOptions {
    return {
      useHTML: true,
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor: 'transparent',
      borderRadius: 10,
      shadow: { color: 'rgba(0,0,0,0.08)', offsetX: 0, offsetY: 2, width: 8 },
      style: { fontSize: '11px' },
    };
  }

  private totalAll(items: NowcastingKpiState.Item[]): number {
    return items.reduce((sum, i) => sum + i.levels.reduce((s, l) => s + l.count, 0), 0);
  }

  // ── Public API ─────────────────────────────────────────────

  computeCharts(items: NowcastingKpiState.Item[]): NowcastingKpiState.Charts {
    if (items.length === 0) {
      return {
        treemapOptions: { chart: { type: 'treemap' }, series: [] },
        heatmapOptions: { chart: { type: 'heatmap' }, series: [] },
        sankeyOptions: { chart: { type: 'sankey' }, series: [] },
        radarOptions: { chart: { type: 'area' }, series: [] },
        barOptions: { chart: { type: 'bar' }, series: [] },
        overviewStats: null,
        summaryDonutOptions: { chart: { type: 'pie' }, series: [] },
        summaryBarOptions: { chart: { type: 'bar' }, series: [] },
      };
    }

    return {
      treemapOptions: this.buildTreemap(items),
      heatmapOptions: this.buildHeatmap(items),
      sankeyOptions: this.buildSankey(items),
      radarOptions: this.buildRadar(items),
      barOptions: this.buildBarChart(items),
      overviewStats: this.buildOverviewStats(items),
      summaryDonutOptions: this.buildDonut(items),
      summaryBarOptions: this.buildBar(items),
    };
  }

  // ── Treemap ───────────────────────────────────────────────

  private buildTreemap(items: NowcastingKpiState.Item[]): Highcharts.Options {
    if (items.length === 0) return { chart: { type: 'treemap' }, series: [] };

    const total = this.totalAll(items);
    const data: Highcharts.PointOptionsObject[] = [];

    for (const item of items) {
      const shades = KpiChartService.LEVEL_PALETTE[item.level] ?? ['#90A4AE'];
      const levelTotal = item.levels.reduce((s, l) => s + l.count, 0);
      const pct = total > 0 ? Math.round((levelTotal / total) * 100) : 0;

      data.push({
        name: `Nivel ${item.level} — ${KpiChartService.LEVEL_LABELS[item.level] ?? ''}`,
        id: `lvl-${item.level}`,
        color: KpiChartService.LEVEL_COLORS[item.level] ?? '#6B8476',
        custom: { isParent: true, total: levelTotal, pct },
      });

      item.levels.forEach((level, idx) => {
        if (level.count <= 0) return;
        data.push({
          name: KpiChartService.LAYER_SHORT[level.layerId] ?? level.name,
          parent: `lvl-${item.level}`,
          value: level.count,
          color: shades[idx % shades.length],
          custom: {
            fullName: level.name,
            nivel: item.level,
            total: levelTotal,
            pct: levelTotal > 0 ? Math.round((level.count / levelTotal) * 100) : 0,
          },
        });
      });
    }

    return {
      chart: { backgroundColor: 'transparent', spacing: [2, 2, 2, 2], style: { fontFamily: 'inherit' }, animation: { duration: 400 } },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      tooltip: {
        ...this.tooltipBase(),
        formatter(this: any): string {
          const p = this.point;
          const c = p.options.custom as any;
          if (c?.isParent) {
            return `<b style="font-size:12px">${p.name}</b><br/>Total: <b>${Highcharts.numberFormat(c.total, 0)}</b> (${c.pct}% del total)`;
          }
          return `<b>${c?.fullName ?? p.name}</b><br/>Nivel ${c?.nivel ?? '?'}: <b>${Highcharts.numberFormat(p.value as number, 0)}</b> (${c?.pct ?? 0}%)`;
        },
      },
      series: [{
        type: 'treemap',
        layoutAlgorithm: 'squarified',
        allowDrillToNode: false,
        animationLimit: 1000,
        clip: false,
        dataLabels: {
          enabled: true,
          allowOverlap: false,
          style: { fontSize: '9px', fontWeight: '600', textOutline: '2px contrast' },
        },
        levels: [
          { level: 1, dataLabels: { enabled: true, align: 'left', verticalAlign: 'top', padding: 5, style: { fontSize: '10px', fontWeight: '700', textOutline: 'none', color: '#fff' } }, borderWidth: 3, borderColor: 'rgba(255,255,255,0.7)' },
          { level: 2, dataLabels: { enabled: true, style: { fontSize: '8px', fontWeight: '600', textOutline: '1px contrast' } }, borderWidth: 1, borderColor: 'rgba(255,255,255,0.4)' },
        ],
        data,
      }],
    };
  }

  // ── Heatmap ───────────────────────────────────────────────

  private buildHeatmap(items: NowcastingKpiState.Item[]): Highcharts.Options {

    const layerIds = [...new Set(items.flatMap(i => i.levels.map(l => l.layerId)))].sort();
    const xCategories = layerIds.map(id => KpiChartService.LAYER_SHORT[id] ?? `L${id}`);
    const yCategories = items.map(i => `Nivel ${i.level} · ${KpiChartService.LEVEL_LABELS[i.level] ?? i.level}`);

    const data: [number, number, number][] = [];
    let maxVal = 0;
    const total = this.totalAll(items);

    items.forEach((item, yIdx) => {
      item.levels.forEach(level => {
        const xIdx = layerIds.indexOf(level.layerId);
        if (xIdx >= 0) {
          data.push([xIdx, yIdx, level.count]);
          if (level.count > maxVal) maxVal = level.count;
        }
      });
    });

    return {
      chart: { type: 'heatmap', backgroundColor: 'transparent', spacing: [8, 8, 4, 4], style: { fontFamily: 'inherit' }, animation: { duration: 500 } },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: { categories: xCategories, labels: { style: { fontSize: '9px', fontWeight: '600', color: '#555' }, rotation: 45, y: 4 }, lineColor: '#e5e7eb', tickLength: 0, opposite: true },
      yAxis: { categories: yCategories, title: undefined, labels: { style: { fontSize: '10px', fontWeight: '700' }, x: -4 }, gridLineColor: '#f0f0f0', reversed: true, tickLength: 0 },
      colorAxis: { min: 0, max: maxVal || 1, stops: [[0, '#f0fdf4'], [0.15, '#bbf7d0'], [0.35, '#86efac'], [0.5, '#fbbf24'], [0.7, '#f97316'], [0.85, '#ef4444'], [1, '#991b1b']], labels: { enabled: true, style: { fontSize: '8px', color: '#888' } } },
      legend: { enabled: true, align: 'right', layout: 'vertical', verticalAlign: 'middle', symbolHeight: 140, symbolWidth: 10, title: { text: 'Afectados', style: { fontSize: '8px', fontWeight: '600', color: '#888' } }, itemStyle: { fontSize: '8px' } },
      tooltip: {
        ...this.tooltipBase(),
        formatter(this: any): string {
          const p = this.point;
          const count = p.value as number;
          const pctTotal = total > 0 ? ((count / total) * 100).toFixed(1) : '0';
          return `<b>${yCategories[p.y]}</b> → ${xCategories[p.x]}<br/>Afectados: <b>${Highcharts.numberFormat(count, 0)}</b><br/><span style="color:#888">${pctTotal}% del total</span>`;
        },
      },
      plotOptions: { heatmap: { borderWidth: 2, borderColor: 'rgba(255,255,255,0.85)', borderRadius: 4 } },
      series: [{
        type: 'heatmap',
        name: 'Afectados',
        data,
        nullColor: '#f9fafb',
        dataLabels: { enabled: true, color: '#1a1a1a', style: { fontSize: '10px', fontWeight: '700', textOutline: 'none' }, formatter(this: any) { const v = this.point.value as number; return v === 0 ? '' : Highcharts.numberFormat(v, 0); } },
      }],
      responsive: { rules: [{ condition: { maxWidth: 300 }, chartOptions: { xAxis: { labels: { style: { fontSize: '7px' } } }, legend: { enabled: false } } }] },
    };
  }

  // ── Sankey ────────────────────────────────────────────────

  private buildSankey(items: NowcastingKpiState.Item[]): Highcharts.Options {

    const total = this.totalAll(items);
    const data: [string, string, number][] = [];
    const layerColorMap = new Map<string, string>();
    let layerIdx = 0;

    for (const item of items) {
      const fromLabel = `Nivel ${item.level} — ${KpiChartService.LEVEL_LABELS[item.level] ?? ''}`;
      for (const level of item.levels) {
        if (level.count <= 0) continue;
        const toLabel = KpiChartService.LAYER_SHORT[level.layerId] ?? level.name;
        if (!layerColorMap.has(toLabel)) {
          layerColorMap.set(toLabel, KpiChartService.LAYER_COLORS[layerIdx % KpiChartService.LAYER_COLORS.length]);
          layerIdx++;
        }
        data.push([fromLabel, toLabel, level.count]);
      }
    }

    const nodes = [
      ...items.map(item => ({ id: `Nivel ${item.level} — ${KpiChartService.LEVEL_LABELS[item.level] ?? ''}`, color: KpiChartService.LEVEL_COLORS[item.level] ?? '#6B8476' })),
      ...Array.from(layerColorMap.entries()).map(([name, color]) => ({ id: name, color })),
    ];

    return {
      chart: { backgroundColor: 'transparent', spacing: [4, 4, 4, 4], style: { fontFamily: 'inherit' }, animation: { duration: 500 } },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      tooltip: {
        ...this.tooltipBase(),
        nodeFormatter(this: any): string {
          return `<b>${this.point.name}</b>`;
        },
        pointFormatter(this: any): string {
          const pct = total > 0 ? ((this.weight / total) * 100).toFixed(1) : '0';
          return `${this.fromNode.name} → ${this.toNode.name}: <b>${Highcharts.numberFormat(this.weight, 0)}</b> <span style="color:#888">(${pct}%)</span>`;
        },
      } as any,
      plotOptions: { sankey: { animation: { duration: 500 }, linkOpacity: 0.35, nodeWidth: 16, nodePadding: 10, borderRadius: 3, dataLabels: { enabled: true, allowOverlap: false, crop: false, overflow: 'allow' as const, style: { fontSize: '9px', fontWeight: '600', textOutline: 'none', color: '#333' }, nodeFormat: '{point.name}' } } },
      series: [{ type: 'sankey', name: 'Impacto', keys: ['from', 'to', 'weight'], data, nodes, nodeAlignment: 'top' }],
    };
  }

  // ── Radar ─────────────────────────────────────────────────

  private buildRadar(items: NowcastingKpiState.Item[]): Highcharts.Options {

    const layerIds = [...new Set(items.flatMap(i => i.levels.map(l => l.layerId)))].sort();
    const categories = layerIds.map(id => KpiChartService.LAYER_SHORT[id] ?? `L${id}`);
    const maxVal = Math.max(1, ...items.flatMap(i => i.levels.map(l => l.count)));

    const series: Highcharts.SeriesOptionsType[] = items.map(item => {
      const color = KpiChartService.LEVEL_COLORS[item.level] ?? '#6B8476';
      const data = layerIds.map(id => item.levels.find(l => l.layerId === id)?.count ?? 0);
      return {
        type: 'area' as const,
        name: `Nivel ${item.level} — ${KpiChartService.LEVEL_LABELS[item.level] ?? ''}`,
        data,
        color,
        fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, `${color}33`], [1, 'rgba(255,255,255,0)']] } as any,
        lineWidth: 2.5,
        marker: { enabled: true, radius: 3.5, symbol: 'circle', lineWidth: 1, lineColor: '#fff' },
        states: { hover: { lineWidth: 3 } },
      };
    });

    return {
      chart: { polar: true, backgroundColor: 'transparent', spacing: [8, 8, 8, 8], style: { fontFamily: 'inherit' }, animation: { duration: 500 } },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: true, align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '9px', fontWeight: '600', color: '#555' }, itemHoverStyle: { color: '#222' }, symbolRadius: 3, symbolHeight: 8, symbolWidth: 8, itemDistance: 10 },
      xAxis: { categories, tickmarkPlacement: 'on', lineWidth: 0, gridLineColor: 'rgba(0,0,0,0.05)', gridLineWidth: 1, labels: { style: { fontSize: '9px', fontWeight: '700', color: '#555' }, distance: 14 } },
      yAxis: { gridLineInterpolation: 'polygon', gridLineColor: 'rgba(0,0,0,0.06)', gridLineWidth: 1, lineWidth: 0, min: 0, max: maxVal * 1.1, tickAmount: 4, title: undefined, labels: { enabled: false } },
      tooltip: { ...this.tooltipBase(), shared: true, pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>' },
      series,
    };
  }

  // ── Summary overview stats ──────────────────────────────

  private buildOverviewStats(items: NowcastingKpiState.Item[]): NowcastingKpiState.OverviewStats {
    const total = this.totalAll(items);

    const levels: NowcastingKpiState.LevelSummary[] = items.map(item => {
      const levelTotal = item.levels.reduce((s, l) => s + l.count, 0);
      return {
        level: item.level,
        total: levelTotal,
        pct: total > 0 ? Math.round((levelTotal / total) * 100) : 0,
        color: KpiChartService.LEVEL_COLORS[item.level] ?? '#6B8476',
        layers: item.levels.map(l => ({
          name: l.name,
          short: KpiChartService.LAYER_SHORT[l.layerId] ?? l.name,
          count: l.count,
          pct: levelTotal > 0 ? Math.round((l.count / levelTotal) * 100) : 0,
          layerId: l.layerId,
          error: l.error ?? false,
        })),
      };
    });

    const layerMap = new Map<number, { name: string; short: string; total: number; layerId: number; error: boolean }>();
    for (const item of items) {
      for (const l of item.levels) {
        const failed = l.error ?? false;
        const existing = layerMap.get(l.layerId);
        if (existing) {
          existing.total += l.count;
          existing.error = existing.error && failed;
        } else {
          layerMap.set(l.layerId, { name: l.name, short: KpiChartService.LAYER_SHORT[l.layerId] ?? l.name, total: l.count, layerId: l.layerId, error: failed });
        }
      }
    }
    const layerTotals = [...layerMap.values()]
      .map(l => {
        const isTerritorial = l.layerId in KpiChartService.TERRITORIAL_LAYER_IDS;
        const byLevel: Record<number, number> | undefined = isTerritorial
          ? Object.fromEntries(
              [2, 3, 4]
                .map(lv => [lv, this.uniqueLevelTotals.get(lv)?.get(l.layerId)?.size ?? 0]),
            )
          : undefined;
        const uniqueTotal = isTerritorial
          ? new Set([
              ...(this.uniqueLevelTotals.get(2)?.get(l.layerId) ?? []),
              ...(this.uniqueLevelTotals.get(3)?.get(l.layerId) ?? []),
              ...(this.uniqueLevelTotals.get(4)?.get(l.layerId) ?? []),
            ]).size
          : l.total;
        return {
          ...l,
          total: uniqueTotal,
          pct: total > 0 ? Math.round((uniqueTotal / total) * 100) : 0,
          byLevel,
          deduped: isTerritorial,
        };
      })
      .sort((a, b) => b.total - a.total);

    return { total, levels, layerTotals };
  }

  /**
   * Unidades administrativas únicas por [nivel][layerId], resueltas por
   * el notifier (dedupe en cliente sobre los campos de ubicación).
   */
  uniqueLevelTotals = new Map<number, Map<number, Set<string>>>();

  // ── Summary donut (severity distribution) ────────────────

  private buildDonut(items: NowcastingKpiState.Item[]): Highcharts.Options {
    const stats = this.buildOverviewStats(items);
    if (stats.total === 0) return { chart: { type: 'pie' }, series: [] };

    return {
      chart: { type: 'pie', backgroundColor: 'transparent', spacing: [0, 0, 0, 0], style: { fontFamily: 'inherit' }, animation: { duration: 400 }, height: 160 },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      tooltip: {
        ...this.tooltipBase(),
        pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
      },
      plotOptions: {
        pie: {
          innerSize: '62%',
          borderWidth: 0,
          borderRadius: 4,
          dataLabels: { enabled: true, distance: 8, format: '<b>{point.percentage:.0f}%</b>', style: { fontSize: '9px', fontWeight: '700', textOutline: 'none', color: '#444' } },
          states: { hover: { halo: { size: 6, opacity: 0.15 } } },
        },
      },
      series: [{
        type: 'pie',
        name: 'Distribución',
        data: stats.levels
          .filter(l => l.total > 0)
          .map(l => ({
            name: `Nivel ${l.level} · ${KpiChartService.LEVEL_LABELS[l.level] ?? l.level}`,
            y: l.total,
            color: l.color,
          })),
      }],
    };
  }

  // ── Summary horizontal bar (geographic totals) ───────────

  private buildBar(items: NowcastingKpiState.Item[]): Highcharts.Options {
    const stats = this.buildOverviewStats(items);
    if (stats.layerTotals.length === 0) return { chart: { type: 'bar' }, series: [] };

    const filtered = stats.layerTotals.filter(l => l.total > 0);
    if (filtered.length === 0) return { chart: { type: 'bar' }, series: [] };

    const cats = filtered.map(l => l.short);
    const maxVal = Math.max(1, ...filtered.map(l => l.total));

    return {
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        spacing: [4, 8, 4, 4],
        style: { fontFamily: 'inherit' },
        animation: { duration: 400 },
        height: Math.max(120, filtered.length * 28 + 20),
      },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories: cats,
        labels: { style: { fontSize: '9px', fontWeight: '600', color: '#555' } },
        lineColor: 'transparent',
        tickLength: 0,
      },
      yAxis: {
        visible: false,
        min: 0,
        max: maxVal * 1.15,
      },
      legend: { enabled: false },
      tooltip: {
        ...this.tooltipBase(),
        formatter(this: any): string {
          const p = this.point;
          const pct = stats.total > 0 ? ((p.y as number / stats.total) * 100).toFixed(1) : '0';
          return `<b>${p.category}</b><br/>Afectados: <b>${Highcharts.numberFormat(p.y as number, 0)}</b><br/><span style="color:#888">${pct}% del total</span>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 3,
          borderWidth: 0,
          pointPadding: 0.15,
          groupPadding: 0,
          colorByPoint: true,
          colors: filtered.map((_, i) => {
            const palette = ['#5C6BC0', '#26A69A', '#66BB6A', '#FFA726', '#EF5350', '#AB47BC', '#78909C', '#8D6E63', '#EC407A'];
            return palette[i % palette.length];
          }),
          dataLabels: {
            enabled: true,
            align: 'left',
            inside: false,
            x: 4,
            style: { fontSize: '9px', fontWeight: '700', textOutline: 'none', color: '#333' },
            formatter(this: any) { return Highcharts.numberFormat(this.y as number, 0); },
          },
        },
      },
      series: [{
        type: 'bar',
        name: 'Afectados',
        data: filtered.map(l => l.total),
      }],
    };
  }

  // ── Stacked bar chart ─────────────────────────────────────

  private buildBarChart(items: NowcastingKpiState.Item[]): Highcharts.Options {
    const LEVEL_LABELS: Record<number, string> = { 2: 'Moderado', 3: 'Fuerte', 4: 'Muy Fuerte' };
    const categories = items.map(i => LEVEL_LABELS[i.level] ?? `Nivel ${i.level}`);

    const layerMap = new Map<number, { name: string; data: number[] }>();
    for (const item of items) {
      for (const level of item.levels) {
        if (!layerMap.has(level.layerId)) {
          layerMap.set(level.layerId, { name: level.name, data: [] });
        }
        layerMap.get(level.layerId)!.data.push(level.count);
      }
    }

    const series: Highcharts.SeriesOptionsType[] = [];
    let colorIdx = 0;
    for (const [, layer] of layerMap) {
      series.push({
        type: 'bar',
        name: layer.name,
        data: layer.data,
        color: KpiChartService.LAYER_COLORS[colorIdx % KpiChartService.LAYER_COLORS.length],
      });
      colorIdx++;
    }

    return {
      chart: { backgroundColor: 'transparent', spacing: [2, 8, 2, 2], style: { fontFamily: 'inherit' } },
      title: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: { categories, labels: { style: { fontSize: '10px', fontWeight: '600', color: '#333' } }, lineColor: '#e5e7eb' },
      yAxis: { min: 0, title: undefined, gridLineColor: '#e5e7eb', gridLineDashStyle: 'Dot', labels: { style: { fontSize: '9px', color: '#999' } } },
      legend: { enabled: true, itemStyle: { fontSize: '9px', fontWeight: '500', color: '#666' }, itemHoverStyle: { color: '#333' }, symbolRadius: 2, itemDistance: 8 },
      tooltip: {
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderColor: 'transparent',
        borderRadius: 8,
        shadow: { color: 'rgba(0,0,0,0.08)', offsetX: 0, offsetY: 2, width: 8 },
        style: { fontSize: '11px' },
        headerFormat: '<b>{point.key}</b><br/>',
        pointFormat: '{series.name}: <b>{point.y}</b>',
      },
      plotOptions: { bar: { borderRadius: 3, pointPadding: 0.05, groupPadding: 0.1, stacking: 'normal', borderWidth: 0 } },
      series,
    };
  }
}
